'use strict';

// DHA Gallery index loader (WI-27, see docs/work-items.md). Loads the
// COMPLETE set of published Gallery Builds from the paginated JSON view
// templates/page.gallery-index.liquid, so every Gallery surface (landing
// discovery, product-page teaser, Build Detail related builds) sees all
// builds instead of the first 50 that Liquid can read on its own.
//
// Shopify gives metaobject lists their own generated pagination query
// parameter, so page URLs can't be built by hand: each response carries a
// `next` URL (null on the last page) and this loader simply follows it. No
// page count or build count is hardcoded — the set grows with the store.
// MAX_PAGES exists only as a runaway guard (50 pages = 2,500 builds); if it
// ever trips, `complete` is false and a console warning says so rather than
// silently truncating.
//
// Exposes window.DHAGalleryIndex.load() -> Promise<{ builds, catalog,
// total, complete }>. Results are cached in sessionStorage for a few
// minutes (product and detail pages fetch it on every view); `fresh: true`
// bypasses the cache. Every storage access is wrapped — storage can be
// unavailable or throw, and nothing here may depend on it.

(function () {
  if (window.DHAGalleryIndex) return;

  var FIRST_URL = '/pages/gallery-builds?view=gallery-index';
  var PATH_PREFIX = '/pages/gallery-builds';
  var MAX_PAGES = 50;
  var CACHE_KEY = 'dha-gallery-index-v1';
  var CACHE_TTL_MS = 3 * 60 * 1000;
  var inflight = null;

  function readCache() {
    try {
      var raw = window.sessionStorage.getItem(CACHE_KEY);
      if (!raw) return null;
      var entry = JSON.parse(raw);
      if (!entry || Date.now() - entry.at > CACHE_TTL_MS) return null;
      return entry.data;
    } catch (error) {
      return null;
    }
  }

  function writeCache(data) {
    try {
      window.sessionStorage.setItem(CACHE_KEY, JSON.stringify({ at: Date.now(), data: data }));
    } catch (error) {
      /* storage unavailable or full — the index still works without it */
    }
  }

  function fetchPage(url) {
    return fetch(url, { credentials: 'same-origin' }).then(function (res) {
      if (!res.ok) throw new Error('Gallery index request failed: ' + res.status);
      return res.text();
    }).then(function (text) {
      return JSON.parse(text);
    });
  }

  function isSafeNext(url) {
    return typeof url === 'string' && url.indexOf(PATH_PREFIX) === 0;
  }

  function loadAll() {
    var builds = [];
    var seen = {};
    var catalog = [];
    var total = 0;
    var pages = 0;

    function step(url) {
      return fetchPage(url).then(function (data) {
        pages++;
        if (pages === 1) {
          catalog = data.catalog || [];
          total = data.total || 0;
        }
        (data.builds || []).forEach(function (b) {
          var key = b.sid || b.url;
          if (seen[key]) return;
          seen[key] = true;
          builds.push(b);
        });
        if (isSafeNext(data.next)) {
          if (pages >= MAX_PAGES) {
            if (window.console && console.warn) console.warn('DHAGalleryIndex: stopped at ' + MAX_PAGES + ' pages; index is incomplete.');
            return false;
          }
          return step(data.next);
        }
        return true;
      });
    }

    return step(FIRST_URL).then(function (complete) {
      return { builds: builds, catalog: catalog, total: total, complete: complete };
    });
  }

  function load(options) {
    var fresh = options && options.fresh;
    if (!fresh) {
      var cached = readCache();
      if (cached) return Promise.resolve(cached);
    }
    if (inflight) return inflight;
    inflight = loadAll().then(function (data) {
      inflight = null;
      writeCache(data);
      return data;
    }, function (error) {
      inflight = null;
      throw error;
    });
    return inflight;
  }

  window.DHAGalleryIndex = { load: load };
})();
